;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import React from "react"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import FindAGroupIcon from "../icons/FindAGroupIcon"
import BecomeAGroupLeaderIcon from "../icons/BecomeAGroupLeaderIcon"
import NewsLetterIcon from "../icons/NewsLetterIcon"
import AccountIcon from "../icons/AccountIcon"
import LogoutIcon from "../icons/LogoutIcon"
import SignUpLoginIcon from "../icons/SignUpLoginIcon"
import GroupsILeadIcon from "../icons/GroupsILeadIcon"
import ProfileIcon from "../icons/ProfileIcon"
import MessagesIcon from "../icons/MessagesIcon"
import NotificationsIcon from "../icons/NotificationsIcon"
import CountBadge from "../CountBadge"
import AdminNavIcon from "../../../AdminNavIcon"
import { ArticleGlyph, BusinessGlyph } from "../icons/glyphs"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { LEADER_ACTIVE_STATE } from "../../../../../lib/constants"
import { adminNavItem, type AdminNavKey } from "../../../../../lib/admin/adminNav"
import updateIsViewWebsiteAsMemberOrLeader from "../../../../../graphql/mutations/updateIsViewWebsiteAsMemberOrLeader"
import {
  TOP_NAV_MENU_TYPE_ACCOUNT,
  TOP_NAV_MENU_TYPE_BECOME_GROUP_LEADER,
  TOP_NAV_MENU_TYPE_FIND_A_GROUP,
  TOP_NAV_MENU_TYPE_GROUPS_I_LEAD,
  TOP_NAV_MENU_TYPE_LOGOUT,
  TOP_NAV_MENU_TYPE_NEWSLETTER_SIGN_UP,
  TOP_NAV_MENU_TYPE_NEWSLETTER_ACCOUNT_SETTING,
  TOP_NAV_MENU_TYPE_PROFILE,
  TOP_NAV_MENU_TYPE_ORGANIZATION_PAGE,
  TOP_NAV_MENU_TYPE_SIGN_UP_LOGIN,
  TOP_NAV_MENU_TYPE_POST_A_NEW_GROUP,
  TOP_NAV_MENU_TYPE_ARTICLE_STUDIO,
  TOP_NAV_MENU_TYPE_MESSAGES,
  TOP_NAV_MENU_TYPE_NOTIFICATIONS,
  TOP_NAV_MENU_TYPE_BY_ADMIN_NAV_KEY,
} from "../constants"
import { ZESTLIFE_BLUE, ZESTLIFE_BLUE_SOFT_BG } from "../zestlifeDropdownTokens"

/**
 * Badge pill offsets replicating the old StyledBadge transform overrides:
 * translate(-1px, -11px), and translate(1px, -12px) at >=600px.
 */
const MENU_BADGE_OFFSET =
  "translate-x-[-1px] translate-y-[-11px] min-[600px]:translate-x-[1px] min-[600px]:translate-y-[-12px]"

/**
 * `[&_svg]:mr-3` replicates the old MUI Menu rule
 * `.MuiMenuItem-root .MuiSvgIcon-root { margin-right: 12px }` that applied to
 * every icon inside a menu row.
 */
const ICON_CONTAINER =
  "flex justify-center items-center min-[600px]:ml-[9px] min-[600px]:mr-[20px] w-[27px] [&_svg]:mr-3"

/**
 * Row base replicating the MUI MenuItem look: 6px/16px padding, 48px
 * min-height under 600px, hover/keyboard highlight rgba(0,0,0,0.04), pressed
 * highlight = primary #00B2FF at selected opacity.
 */
const ITEM_BASE_CLASSES =
  "cursor-pointer gap-0 rounded-none px-4 py-1.5 min-h-12 min-[600px]:min-h-0 font-roboto text-[16px] text-inherit focus:bg-[rgba(0,0,0,0.04)] focus:text-inherit data-highlighted:bg-[rgba(0,0,0,0.04)] active:bg-[rgba(0,178,255,0.08)]"

/**
 * Current-page row (admin dropdown): tinted fill plus a dark left bar, so the
 * state doesn't rely on color alone.
 */
const ACTIVE_ITEM_CLASSES =
  "bg-[rgba(0,178,255,0.1)] shadow-[inset_3px_0_0_#092C3C] focus:bg-[rgba(0,178,255,0.16)] data-highlighted:bg-[rgba(0,178,255,0.16)]"

/** Admin row types → adminNav keys (label, href and icon live in adminNav). */
const ADMIN_NAV_KEY_BY_MENU_TYPE: Record<string, AdminNavKey> = Object.fromEntries(
  Object.entries(TOP_NAV_MENU_TYPE_BY_ADMIN_NAV_KEY).map(([key, type]) => [
    type,
    key as AdminNavKey,
  ]),
)

/** Matches `.dd-row-icon` SVGs in zestlife_dropdown_white.html (#18B4E8 @ 0.7). */
function ZestlifeRowIconTint({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center justify-center leading-[0] [&_svg]:opacity-70 [&_svg_path]:fill-[rgba(24,180,232,0.7)]">
      {children}
    </span>
  )
}

export type MenuItemMessageBadgeStyle = "icon" | "trailing"

// TODO: badge count should is only be for leader. maybe make this more general?
export default function MenuItem({
  topNavMenuItemType,
  handleLogout,
  badgeCount,
  messageBadgeStyle = "icon",
  logoutVariant = "default",
  iconMuted = false,
  dense = false,
  iconTintZestlife = false,
  organizationOrgId,
  active = false,
  ariaCurrent = "page",
  className,
}: {
  topNavMenuItemType: string
  handleLogout?: () => void | Promise<void>
  badgeCount?: number
  messageBadgeStyle?: MenuItemMessageBadgeStyle
  logoutVariant?: "default" | "danger"
  iconMuted?: boolean
  /** Smaller type and padding (account dropdown) */
  dense?: boolean
  /** Provider dropdown primary rows: #18B4E8 @ 70% like zestlife_dropdown_white.html */
  iconTintZestlife?: boolean
  /** Required when `topNavMenuItemType` is ORGANIZATION_PAGE */
  organizationOrgId?: string
  /** Marks the row as the current page (aria-current + active style) */
  active?: boolean
  /** aria-current value when active: "true" when inside the item's section */
  ariaCurrent?: "page" | "true"
  /** Merged last onto the row classes */
  className?: string
}) {
  const router = useRouter()
  const iconWrapClass = `${ICON_CONTAINER}${iconMuted ? " opacity-[0.55]" : ""}`
  const adminKey = ADMIN_NAV_KEY_BY_MENU_TYPE[topNavMenuItemType]
  const adminItem = adminKey ? adminNavItem(adminKey) : null

  const tintZestlife = (node: React.ReactNode) =>
    iconTintZestlife ? <ZestlifeRowIconTint>{node}</ZestlifeRowIconTint> : node

  const getIcon = () => {
    if (adminItem) {
      return (
        <div className={iconWrapClass}>
          <AdminNavIcon name={adminItem.icon} sx={{ color: "rgba(0, 0, 0, 0.6)" }} />
        </div>
      )
    }
    switch (topNavMenuItemType) {
      case TOP_NAV_MENU_TYPE_ACCOUNT:
        return (
          <div className={iconWrapClass}>
            <AccountIcon />
          </div>
        )
      case TOP_NAV_MENU_TYPE_POST_A_NEW_GROUP:
      case TOP_NAV_MENU_TYPE_BECOME_GROUP_LEADER:
        return (
          <div className={iconWrapClass}>
            {tintZestlife(<BecomeAGroupLeaderIcon />)}
          </div>
        )
      case TOP_NAV_MENU_TYPE_FIND_A_GROUP:
        return (
          <div className={iconWrapClass}>{tintZestlife(<FindAGroupIcon />)}</div>
        )
      case TOP_NAV_MENU_TYPE_ARTICLE_STUDIO:
        return (
          <div className={iconWrapClass}>
            {tintZestlife(
              <ArticleGlyph size={24} style={{ color: "rgba(0, 0, 0, 0.6)" }} />,
            )}
          </div>
        )
      case TOP_NAV_MENU_TYPE_MESSAGES: {
        const zestlifeMsgStyle = iconTintZestlife
          ? { color: ZESTLIFE_BLUE, opacity: 0.7 }
          : undefined
        return (
          <div className={iconWrapClass}>
            {messageBadgeStyle === "icon" && badgeCount ? (
              <CountBadge count={badgeCount} offsetClassName={MENU_BADGE_OFFSET}>
                <MessagesIcon style={zestlifeMsgStyle} />
              </CountBadge>
            ) : (
              <MessagesIcon style={zestlifeMsgStyle} />
            )}
          </div>
        )
      }
      case TOP_NAV_MENU_TYPE_NOTIFICATIONS:
        return (
          <div className={iconWrapClass}>
            {badgeCount ? (
              <CountBadge count={badgeCount} offsetClassName={MENU_BADGE_OFFSET}>
                <NotificationsIcon />
              </CountBadge>
            ) : (
              <NotificationsIcon />
            )}
          </div>
        )
      case TOP_NAV_MENU_TYPE_GROUPS_I_LEAD:
        return (
          <div className={iconWrapClass}>
            {badgeCount ? (
              <CountBadge count={badgeCount} offsetClassName={MENU_BADGE_OFFSET}>
                {tintZestlife(<GroupsILeadIcon />)}
              </CountBadge>
            ) : (
              tintZestlife(<GroupsILeadIcon />)
            )}
          </div>
        )
      case TOP_NAV_MENU_TYPE_LOGOUT:
        return (
          <div className={`${ICON_CONTAINER} relative left-[1px]`}>
            <LogoutIcon />
          </div>
        )
      case TOP_NAV_MENU_TYPE_NEWSLETTER_SIGN_UP:
      case TOP_NAV_MENU_TYPE_NEWSLETTER_ACCOUNT_SETTING:
        return (
          <div className={iconWrapClass}>{tintZestlife(<NewsLetterIcon />)}</div>
        )
      case TOP_NAV_MENU_TYPE_ORGANIZATION_PAGE:
        return (
          <div className={iconWrapClass}>
            {tintZestlife(
              <BusinessGlyph size={24} style={{ color: "rgba(0, 0, 0, 0.6)" }} />,
            )}
          </div>
        )
      case TOP_NAV_MENU_TYPE_PROFILE:
        return (
          <div className={iconWrapClass}>
            <ProfileIcon />
          </div>
        )
      case TOP_NAV_MENU_TYPE_SIGN_UP_LOGIN:
        return (
          <div className={iconWrapClass}>
            <SignUpLoginIcon />
          </div>
        )
    }
  }

  const getText = () => {
    if (adminItem) return adminItem.label
    switch (topNavMenuItemType) {
      case TOP_NAV_MENU_TYPE_ACCOUNT:
        return "Account"
      case TOP_NAV_MENU_TYPE_POST_A_NEW_GROUP:
        return "Post a new group"
      case TOP_NAV_MENU_TYPE_BECOME_GROUP_LEADER:
        return "Providers"
      case TOP_NAV_MENU_TYPE_FIND_A_GROUP:
        return "Find a group"
      case TOP_NAV_MENU_TYPE_ARTICLE_STUDIO:
        return "Article Studio"
      case TOP_NAV_MENU_TYPE_GROUPS_I_LEAD:
        return "Provider dashboard"
      case TOP_NAV_MENU_TYPE_LOGOUT:
        return "Logout"
      case TOP_NAV_MENU_TYPE_NEWSLETTER_ACCOUNT_SETTING:
      case TOP_NAV_MENU_TYPE_NEWSLETTER_SIGN_UP:
        return "Newsletter"
      case TOP_NAV_MENU_TYPE_ORGANIZATION_PAGE:
        return "Organization Profile"
      case TOP_NAV_MENU_TYPE_PROFILE:
        return "Profile"
      case TOP_NAV_MENU_TYPE_SIGN_UP_LOGIN:
        return "Sign up / log in"
      case TOP_NAV_MENU_TYPE_MESSAGES:
        return "Messages"
      case TOP_NAV_MENU_TYPE_NOTIFICATIONS:
        return "Notifications"
    }
  }

  const getIconAndText = () => {
    const showTrailingMsgBadge =
      topNavMenuItemType === TOP_NAV_MENU_TYPE_MESSAGES &&
      messageBadgeStyle === "trailing" &&
      badgeCount != null &&
      badgeCount > 0

    const trailingChip = showTrailingMsgBadge ? (
      <span
        key="msg-trail-badge"
        className="font-roboto shrink-0 rounded-[20px] px-[7px] py-[2px] text-[10px] font-medium whitespace-nowrap"
        style={{ backgroundColor: ZESTLIFE_BLUE_SOFT_BG, color: ZESTLIFE_BLUE }}
      >
        {badgeCount} new
      </span>
    ) : null

    return [
      <div key={`list-item-icon-${topNavMenuItemType}`}>{getIcon()}</div>,
      <div
        key={`list-item-text-${topNavMenuItemType}`}
        className={`font-roboto min-w-0 flex-1${trailingChip ? " mr-2" : ""}`}
      >
        <div
          className={`font-roboto font-medium min-[600px]:mr-[15px] ${dense ? "text-[14px]" : "text-[16px]"}`}
        >
          {getText()}
        </div>
      </div>,
      trailingChip,
    ].filter(Boolean) as React.ReactNode[]
  }

  const itemClasses = cn(
    ITEM_BASE_CLASSES,
    dense && "min-h-10 rounded-[9px] py-[7px] min-[600px]:min-h-10",
    // Literal values = ZESTLIFE_LOGOUT_RED / ZESTLIFE_LOGOUT_RED_SOFT_BG
    // (Tailwind can't extract classes built from template literals).
    topNavMenuItemType === TOP_NAV_MENU_TYPE_LOGOUT &&
      logoutVariant === "danger" &&
      "text-[#D14343] focus:bg-[#FDECEC] data-highlighted:bg-[#FDECEC] [&_svg]:text-[#D14343] [&_svg_path]:fill-[#D14343]",
    active && ACTIVE_ITEM_CLASSES,
    className,
  )

  const handleOnClick = () => {
    switch (topNavMenuItemType) {
      case TOP_NAV_MENU_TYPE_LOGOUT:
        return handleLogout?.()
      case TOP_NAV_MENU_TYPE_GROUPS_I_LEAD:
        updateIsViewWebsiteAsMemberOrLeader(LEADER_ACTIVE_STATE)
        router.push("/dashboard#groups")
        return
      case TOP_NAV_MENU_TYPE_PROFILE:
        updateIsViewWebsiteAsMemberOrLeader(LEADER_ACTIVE_STATE)
        router.push("/profile-overview")
        return
    }
  }
  const getHref = (): string | false => {
    if (adminItem) return adminItem.href
    switch (topNavMenuItemType) {
      case TOP_NAV_MENU_TYPE_ORGANIZATION_PAGE:
        return organizationOrgId ? `/organization/${organizationOrgId}` : false
      case TOP_NAV_MENU_TYPE_ACCOUNT:
        return "/account"
      case TOP_NAV_MENU_TYPE_POST_A_NEW_GROUP:
        return "/create-group"
      case TOP_NAV_MENU_TYPE_BECOME_GROUP_LEADER:
        return "/therapists"
      case TOP_NAV_MENU_TYPE_FIND_A_GROUP:
        return "/"
      case TOP_NAV_MENU_TYPE_ARTICLE_STUDIO:
        return "/dashboard/studio"
      case TOP_NAV_MENU_TYPE_NEWSLETTER_SIGN_UP:
        return "/signup?newsletter=true"
      case TOP_NAV_MENU_TYPE_NEWSLETTER_ACCOUNT_SETTING:
        return "/account/notifications"
      case TOP_NAV_MENU_TYPE_SIGN_UP_LOGIN:
        return "/login"
      case TOP_NAV_MENU_TYPE_MESSAGES:
        return "/messages"
      case TOP_NAV_MENU_TYPE_NOTIFICATIONS:
        return "/notifications"
    }
    return false
  }

  // Unknown/retired types (e.g. GROUPS_I_ATTEND) used to render an empty MUI
  // row; render nothing instead.
  if (!getText()) {
    return null
  }

  const href = getHref()

  return href ? (
    <DropdownMenuItem asChild className={itemClasses} key={getText()}>
      <Link
        href={href}
        aria-current={active ? ariaCurrent : undefined}
        className="text-inherit no-underline"
      >
        {getIconAndText()}
      </Link>
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem
      key={getText()}
      className={itemClasses}
      onSelect={() => handleOnClick()}
    >
      {getIconAndText()}
    </DropdownMenuItem>
  )
}
